// ICS User Management — Admin-only page to browse ICS employees and enable them
// in the production system with a temporary password and assigned role

import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Search, UserPlus, UserX, Users, Copy, Check, Shield, Trash2, AlertTriangle, ChevronDown, KeyRound } from 'lucide-react';
import { authService } from '../../services/api';
import { userService } from '../../services/userService';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';

interface IcsUser {
  user_id: string;
  employee_number: string | null;
  display_name: string | null;
  full_name: string | null;
  production_user_id: string | null;
  production_user_level: string | null;
  production_disabled: string | null;
  must_change_password: boolean | null;
  production_updated_at: string | null;
}

interface IcsAvailableUser {
  user_id: string;
  ics_user_level: string;
  employee_number: string | null;
  ics_disabled: string | null;
  display_name: string | null;
  full_name: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  A: 'Admin',
  S: 'Supervisor',
  M: 'Manager',
  O: 'Operator',
  R: 'Route',
  W: 'Warehouse',
};

const IcsUserManagement: React.FC = () => {
  const { t } = useLanguage();
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  // Managers can assign O, S, M but not A. Admins can assign all.
  const availableRoles = authUser?.role === 'M' ? ['O', 'S', 'M'] : ['O', 'S', 'M', 'A'];

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [selectedIcsUser, setSelectedIcsUser] = useState<(IcsUser | IcsAvailableUser) | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('O');
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<IcsUser | null>(null);
  const [roleEditUserId, setRoleEditUserId] = useState<string | null>(null);
  const [roleDropdownPos, setRoleDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState<IcsUser | null>(null);
  const [resetTempPwd, setResetTempPwd] = useState<string | null>(null);
  const [copiedResetPwd, setCopiedResetPwd] = useState(false);

  // Fetch production users for the main table
  const { data: icsUsers = [], isLoading } = useQuery(
    ['ics-users'],
    () => authService.getIcsUsers(),
    { refetchOnWindowFocus: false }
  );

  // Fetch ICS-available users for the Add modal (only when modal is open)
  const { data: icsAvailableUsers = [] } = useQuery(
    ['ics-available-users'],
    () => authService.getIcsAvailableUsers(),
    { refetchOnWindowFocus: false, enabled: showAddModal }
  );

  // Enable user mutation
  const enableMutation = useMutation(
    (data: { userId: string; role: string }) => authService.enableUser(data.userId, data.role),
    {
      onSuccess: (response) => {
        const pwd = response.data?.tempPassword;
        setTempPassword(pwd);
        toast.success(t('icsUserManagement.userEnabled'));
        queryClient.invalidateQueries(['ics-users']);
        queryClient.invalidateQueries(['ics-available-users']);
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.message || t('common.error');
        toast.error(msg);
      },
    }
  );

  // Disable user mutation
  const disableMutation = useMutation(
    (data: { userId: string; disabled: boolean }) => authService.disableUser(data.userId, data.disabled),
    {
      onSuccess: () => {
        toast.success(t('icsUserManagement.statusUpdated'));
        queryClient.invalidateQueries(['ics-users']);
      },
      onError: () => {
        toast.error(t('common.error'));
      },
    }
  );

  // Delete user mutation
  const deleteMutation = useMutation(
    (userId: string) => authService.deleteProductionUser(userId),
    {
      onSuccess: () => {
        toast.success(t('icsUserManagement.userDeleted'));
        queryClient.invalidateQueries(['ics-users']);
        queryClient.invalidateQueries(['ics-available-users']);
        setShowDeleteModal(false);
        setUserToDelete(null);
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.message || t('common.error');
        toast.error(msg);
      },
    }
  );

  // Change role mutation
  const changeRoleMutation = useMutation(
    (data: { userId: string; role: string }) => userService.updateUser(data.userId, { role: data.role as any }),
    {
      onSuccess: () => {
        toast.success(t('icsUserManagement.roleUpdated'));
        queryClient.invalidateQueries(['ics-users']);
        setRoleEditUserId(null);
        setRoleDropdownPos(null);
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.message || t('common.error');
        toast.error(msg);
      },
    }
  );

  // Reset temp password mutation
  const resetPasswordMutation = useMutation(
    (userId: string) => authService.resetTempPassword(userId),
    {
      onSuccess: (response) => {
        const pwd = response.data?.tempPassword;
        setResetTempPwd(pwd);
        toast.success(t('icsUserManagement.passwordReset'));
        queryClient.invalidateQueries(['ics-users']);
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.message || t('common.error');
        toast.error(msg);
      },
    }
  );

  // Filter and search — all rows are production users now
  const filteredUsers = useMemo(() => {
    let result = (icsUsers as IcsUser[]);

    // "All" = all production users, "Active" = not disabled, "Inactive" = disabled
    if (filterStatus === 'active') {
      result = result.filter(u => u.production_disabled !== 'Y');
    } else if (filterStatus === 'inactive') {
      result = result.filter(u => u.production_disabled === 'Y');
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(u =>
        u.user_id?.toLowerCase().includes(q) ||
        u.display_name?.toLowerCase().includes(q) ||
        u.employee_number?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [icsUsers, filterStatus, search]);

  const getStatus = (user: IcsUser): { label: string; color: string } => {
    if (user.production_disabled === 'Y') {
      return { label: t('icsUserManagement.disabled'), color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    }
    if (user.must_change_password) {
      return { label: t('icsUserManagement.pendingPassword'), color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' };
    }
    return { label: t('icsUserManagement.active'), color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
  };

  const handleEnable = () => {
    if (!selectedIcsUser) return;
    enableMutation.mutate({ userId: selectedIcsUser.user_id, role: selectedRole });
  };

  const handleCopyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const closeModal = () => {
    setShowEnableModal(false);
    setShowAddModal(false);
    setSelectedIcsUser(null);
    setSelectedRole('O');
    setTempPassword(null);
    setCopiedPassword(false);
  };

  const statusCounts = useMemo(() => {
    const users = icsUsers as IcsUser[];
    return {
      all: users.length,
      active: users.filter(u => u.production_disabled !== 'Y').length,
      inactive: users.filter(u => u.production_disabled === 'Y').length,
    };
  }, [icsUsers]);

  // Available ICS users for the Add modal (not yet in production, fetched separately)
  const availableUsers = useMemo(() => {
    const users = icsAvailableUsers as IcsAvailableUser[];
    if (!addSearch.trim()) return users;
    const q = addSearch.toLowerCase();
    return users.filter(u =>
      u.user_id?.toLowerCase().includes(q) ||
      u.display_name?.toLowerCase().includes(q) ||
      u.employee_number?.toLowerCase().includes(q)
    );
  }, [icsAvailableUsers, addSearch]);

  return (
    <div className="h-full flex flex-col">
      {/* Search, Filters, and Table */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col min-h-0">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <div className="relative min-w-[20rem]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('icsUserManagement.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all' as const, label: t('common.all'), count: statusCounts.all },
              { key: 'active' as const, label: t('icsUserManagement.active'), count: statusCounts.active },
              { key: 'inactive' as const, label: t('userManagement.inactive'), count: statusCounts.inactive },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilterStatus(f.key)}
                className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  filterStatus === f.key
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <button
              onClick={() => {
                setShowAddModal(true);
                setAddSearch('');
                setSelectedIcsUser(null);
                setSelectedRole('O');
                setTempPassword(null);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              {t('icsUserManagement.addUser')}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center p-8 text-gray-400 dark:text-gray-500">
              <div className="text-center">
                <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('icsUserManagement.noResults')}</p>
              </div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('icsUserManagement.userId')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('icsUserManagement.name')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('icsUserManagement.employeeNum')}</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('icsUserManagement.prodRole')}</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('common.status')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('icsUserManagement.lastModified')}</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => {
                  const status = getStatus(user);
                  return (
                    <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-mono text-xs">{user.user_id}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{user.display_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-mono">{user.employee_number || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        {user.production_user_level ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (roleEditUserId === user.user_id) {
                                setRoleEditUserId(null);
                                setRoleDropdownPos(null);
                              } else {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                const dropdownHeight = 140; // approx height of 4 role items
                                const spaceBelow = window.innerHeight - rect.bottom;
                                if (spaceBelow < dropdownHeight) {
                                  // Open upward
                                  setRoleDropdownPos({ top: rect.top - dropdownHeight - 4, left: rect.left + rect.width / 2 });
                                } else {
                                  setRoleDropdownPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
                                }
                                setRoleEditUserId(user.user_id);
                              }
                            }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 px-2 py-1 rounded transition-colors cursor-pointer"
                            title={t('icsUserManagement.changeRole')}
                          >
                            <Shield className="h-3 w-3" />
                            {ROLE_LABELS[user.production_user_level] || user.production_user_level}
                            <ChevronDown className="h-3 w-3 opacity-50" />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-sm">
                        {user.production_updated_at ? new Date(user.production_updated_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => {
                              setPasswordUser(user);
                              setResetTempPwd(null);
                              setCopiedResetPwd(false);
                              setShowPasswordModal(true);
                            }}
                            className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-900/30 rounded transition-colors"
                            title={t('icsUserManagement.resetPassword')}
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => disableMutation.mutate({
                              userId: user.user_id,
                              disabled: user.production_disabled !== 'Y'
                            })}
                            disabled={disableMutation.isLoading}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                              user.production_disabled === 'Y'
                                ? 'text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/30 dark:hover:bg-green-900/50'
                                : 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/30 dark:hover:bg-red-900/50'
                            }`}
                          >
                            {user.production_disabled === 'Y' ? (
                              <><UserPlus className="h-3.5 w-3.5" /> {t('icsUserManagement.reEnable')}</>
                            ) : (
                              <><UserX className="h-3.5 w-3.5" /> {t('icsUserManagement.disable')}</>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setUserToDelete(user);
                              setShowDeleteModal(true);
                            }}
                            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded transition-colors"
                            title={t('icsUserManagement.deleteUser')}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Role Change Dropdown (fixed position to avoid overflow clipping) */}
      {roleEditUserId && roleDropdownPos && (() => {
        const editUser = (icsUsers as IcsUser[]).find(u => u.user_id === roleEditUserId);
        if (!editUser) return null;
        return (
          <>
            <div className="fixed inset-0 z-40" onClick={() => { setRoleEditUserId(null); setRoleDropdownPos(null); }} />
            <div
              className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-2 min-w-[140px]"
              style={{ top: roleDropdownPos.top, left: roleDropdownPos.left, transform: 'translateX(-50%)' }}
            >
              {availableRoles.map(role => (
                <button
                  key={role}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (role !== editUser.production_user_level) {
                      changeRoleMutation.mutate({ userId: editUser.user_id, role });
                    } else {
                      setRoleEditUserId(null);
                      setRoleDropdownPos(null);
                    }
                  }}
                  disabled={changeRoleMutation.isLoading}
                  className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors ${
                    role === editUser.production_user_level
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {ROLE_LABELS[role]}
                  {role === editUser.production_user_level && <Check className="h-3 w-3 inline ml-1" />}
                </button>
              ))}
            </div>
          </>
        );
      })()}

      {/* Add User Modal — browse ICS directory and enable */}
      {showAddModal && !selectedIcsUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('icsUserManagement.addUser')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  placeholder={t('icsUserManagement.searchPlaceholder')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>

              <div className="h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                {availableUsers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {addSearch.trim() ? t('icsUserManagement.noResults') : t('icsUserManagement.allEnabled')}
                  </div>
                ) : (
                  availableUsers.map((user) => (
                    <button
                      key={user.user_id}
                      onClick={() => {
                        setSelectedIcsUser(user);
                        setSelectedRole(
                          user.ics_user_level === 'A' || user.ics_user_level === 'S' || user.ics_user_level === 'M'
                            ? user.ics_user_level : 'O'
                        );
                        setTempPassword(null);
                        setShowEnableModal(true);
                      }}
                      className="w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {user.display_name || user.user_id}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {user.user_id} {user.employee_number ? `· #${user.employee_number}` : ''} · {ROLE_LABELS[user.ics_user_level] || user.ics_user_level}
                          </div>
                        </div>
                        <UserPlus className="h-4 w-4 text-gray-400" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enable User Modal */}
      {showEnableModal && selectedIcsUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {tempPassword ? t('icsUserManagement.userCreated') : t('icsUserManagement.enableUser')}
              </h3>
            </div>

            <div className="p-4 space-y-4">
              {/* User info */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedIcsUser.display_name || selectedIcsUser.user_id}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('icsUserManagement.userId')}: {selectedIcsUser.user_id} | {t('icsUserManagement.employeeNum')}: {selectedIcsUser.employee_number || '-'}
                </div>
              </div>

              {!tempPassword ? (
                // Role selection step
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('icsUserManagement.assignRole')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableRoles.map(role => (
                      <button
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          selectedRole === role
                            ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-600'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {ROLE_LABELS[role]}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                // Temp password result step
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('icsUserManagement.tempPasswordInfo')}
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="flex-1">
                      <div className="text-xs text-yellow-700 dark:text-yellow-400 mb-1">{t('icsUserManagement.tempPassword')}</div>
                      <div className="text-lg font-mono font-bold text-yellow-800 dark:text-yellow-300">{tempPassword}</div>
                    </div>
                    <button
                      onClick={handleCopyPassword}
                      className="p-2 text-yellow-700 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-200 transition-colors"
                      title={t('common.copy')}
                    >
                      {copiedPassword ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('icsUserManagement.tempPasswordNote')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              {!tempPassword ? (
                <>
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleEnable}
                    disabled={enableMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                  >
                    {enableMutation.isLoading ? t('common.loading') : t('icsUserManagement.enable')}
                  </button>
                </>
              ) : (
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  {t('common.done')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && passwordUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {resetTempPwd ? t('icsUserManagement.passwordResetDone') : t('icsUserManagement.resetPassword')}
              </h3>
            </div>

            <div className="p-4 space-y-4">
              {/* User info */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {passwordUser.display_name || passwordUser.user_id}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('icsUserManagement.userId')}: {passwordUser.user_id} | {t('icsUserManagement.employeeNum')}: {passwordUser.employee_number || '-'}
                </div>
              </div>

              {!resetTempPwd ? (
                /* Confirmation step */
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('icsUserManagement.resetPasswordConfirm')}
                </p>
              ) : (
                /* Show generated temp password */
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="flex-1">
                      <div className="text-xs text-yellow-700 dark:text-yellow-400 mb-1">{t('icsUserManagement.tempPassword')}</div>
                      <div className="text-lg font-mono font-bold text-yellow-800 dark:text-yellow-300">{resetTempPwd}</div>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(resetTempPwd);
                        setCopiedResetPwd(true);
                        setTimeout(() => setCopiedResetPwd(false), 2000);
                      }}
                      className="p-2 text-yellow-700 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-200 transition-colors"
                      title={t('common.copy')}
                    >
                      {copiedResetPwd ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('icsUserManagement.tempPasswordNote')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              {!resetTempPwd ? (
                <>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordUser(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => resetPasswordMutation.mutate(passwordUser.user_id)}
                    disabled={resetPasswordMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                  >
                    {resetPasswordMutation.isLoading ? t('common.loading') : t('icsUserManagement.resetPassword')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordUser(null);
                    setResetTempPwd(null);
                    setCopiedResetPwd(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  {t('common.done')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('icsUserManagement.deleteUser')}
                </h3>
              </div>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('icsUserManagement.deleteWarning')
                  .replace('{{name}}', userToDelete.display_name || userToDelete.user_id)}
              </p>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  if (userToDelete) {
                    deleteMutation.mutate(userToDelete.user_id);
                  }
                }}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                {deleteMutation.isLoading ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IcsUserManagement;
